import { CONVERSATION_PRIORITIES, isConversationPriority } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, ChoiceChips, Icon, Label, Popover, PopoverContent, PopoverTrigger, Select } from "@sagot/ui";
import { useId, useState } from "react";
import { useTeams } from "../../../roles/use-roles";
import { useAgentList } from "../shared/use-engage-data";
import { activeFilterCount, DATE_RANGES, FILTER_CHANNELS, INBOX_SORTS, type ChannelType, type DateRange, type InboxFilters, type InboxSort } from "./inbox-model";
import { useInboxStore } from "./inbox-store";

type MenuFields = Pick<InboxFilters, "channels" | "agentId" | "teamId" | "tag" | "range" | "priority" | "sort">;

function isDateRange(value: string): value is DateRange {
  return DATE_RANGES.some((r) => r === value);
}

function isSort(value: string): value is InboxSort {
  return INBOX_SORTS.some((s) => s === value);
}

/** Filter popover: channel chips, agent, tag (tenant-defined, from conversations), date range. */
export function FilterMenu({ tagOptions }: { tagOptions: readonly string[] }) {
  const { t } = useTranslation();
  const id = useId();
  const filters = useInboxStore((s) => s.filters);
  const applyFilters = useInboxStore((s) => s.applyFilters);
  const resetFilters = useInboxStore((s) => s.resetFilters);
  const agents = useAgentList();
  const teams = useTeams();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<MenuFields>(filters);
  const count = activeFilterCount(filters);

  const onOpenChange = (next: boolean) => {
    if (next) setDraft({ channels: filters.channels, agentId: filters.agentId, teamId: filters.teamId, tag: filters.tag, range: filters.range, priority: filters.priority, sort: filters.sort });
    setOpen(next);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" iconOnly aria-label={count ? t("engage.inbox.filters.openActive", { count }) : t("engage.inbox.filters.open")} className="relative">
          <Icon name="filter" />
          {count ? <span className="absolute right-1 top-1 size-1.5 rounded-pill bg-primary" aria-hidden="true" /> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="space-y-3 p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-fg-subtle">{t("engage.inbox.filters.heading")}</div>
        <div>
          <div className="label">{t("engage.inbox.filters.channel")}</div>
          <ChoiceChips<ChannelType>
            multiple
            variant="badge"
            aria-label={t("engage.inbox.filters.channel")}
            value={draft.channels}
            onValueChange={(channels: ChannelType[]) => {
              setDraft((d) => ({ ...d, channels }));
            }}
            options={FILTER_CHANNELS.map((c) => ({ value: c, label: t(`engage.channels.${c}`) }))}
            className="gap-1.5"
          />
        </div>
        <div>
          <Label htmlFor={`${id}-agent`}>{t("engage.inbox.filters.agent")}</Label>
          <Select id={`${id}-agent`} className="h-9" value={draft.agentId ?? ""} onChange={(e) => { setDraft((d) => ({ ...d, agentId: e.target.value || null })); }}>
            <option value="">{t("engage.allAgents")}</option>
            {(agents.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Select>
        </div>
        {teams.data && teams.data.length > 0 ? (
          <div>
            <Label htmlFor={`${id}-team`}>{t("engage.inbox.filters.team")}</Label>
            <Select id={`${id}-team`} className="h-9" value={draft.teamId ?? ""} onChange={(e) => { setDraft((d) => ({ ...d, teamId: e.target.value || null })); }}>
              <option value="">{t("engage.inbox.filters.anyTeam")}</option>
              {teams.data.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor={`${id}-tag`}>{t("engage.inbox.filters.tag")}</Label>
            <Select id={`${id}-tag`} className="h-9" value={draft.tag ?? ""} onChange={(e) => { setDraft((d) => ({ ...d, tag: e.target.value || null })); }}>
              <option value="">{t("engage.inbox.filters.anyTag")}</option>
              {tagOptions.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor={`${id}-date`}>{t("engage.inbox.filters.date")}</Label>
            <Select id={`${id}-date`} className="h-9" value={draft.range} onChange={(e) => { if (isDateRange(e.target.value)) { const range = e.target.value; setDraft((d) => ({ ...d, range })); } }}>
              {DATE_RANGES.map((r) => (
                <option key={r} value={r}>
                  {t(`engage.inbox.filters.ranges.${r}`)}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label htmlFor={`${id}-priority`}>{t("engage.inbox.filters.priority")}</Label>
            <Select id={`${id}-priority`} className="h-9" value={draft.priority ?? ""} onChange={(e) => { setDraft((d) => ({ ...d, priority: isConversationPriority(e.target.value) ? e.target.value : null })); }}>
              <option value="">{t("engage.inbox.filters.anyPriority")}</option>
              {CONVERSATION_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`engage.inbox.priority.${p}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor={`${id}-sort`}>{t("engage.inbox.filters.sort")}</Label>
            <Select id={`${id}-sort`} className="h-9" value={draft.sort} onChange={(e) => { if (isSort(e.target.value)) { const sort = e.target.value; setDraft((d) => ({ ...d, sort })); } }}>
              {INBOX_SORTS.map((s) => (
                <option key={s} value={s}>
                  {t(`engage.inbox.filters.sorts.${s}`)}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => { resetFilters(); setOpen(false); }}>
            {t("engage.inbox.filters.reset")}
          </Button>
          <Button size="sm" onClick={() => { applyFilters(draft); setOpen(false); }}>
            {t("engage.inbox.filters.apply")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
